import { apiFetch } from '../client';
import type {
    MeProfileResponseLocal,
    UpdateProfileRequestLocal,
    MfaStatusResponseLocal,
    MfaEnrollTotpResponseLocal,
    MfaVerifyTotpEnrollmentRequestLocal,
    MfaChangeMethodRequestLocal,
    MfaChooseEmailResponseLocal,
    MfaChooseSmsResponseLocal,
    MfaOptionsResponseLocal,
} from '../types/MeLocal';
import type { operations } from '../generated/backoffice.gen';
import { normalizeMeProfile } from '../types/meNormalizers';

// DEC-RBAC-081: bodies del identificador secundario de /me, derivados del generado (no hand-rolled).
type AddSecondaryIdentifierBody =
    operations['backofficeAddMySecondaryIdentifier']['requestBody']['content']['application/json'];
type VerifySecondaryIdentifierBody =
    operations['backofficeVerifyMySecondaryIdentifier']['requestBody']['content']['application/json'];

export const meApi = {
    // DEC-RBAC-027: normalizamos el profile para materializar `externalScopeLevels` (default [], filtra
    // valores fuera de KNOWN_SCOPES) — la sesión lo usa para derivar `requireExternalId` en contexto-tenant.
    getProfile: () => apiFetch<MeProfileResponseLocal>('/me/profile').then(normalizeMeProfile),
    updateProfile: (body: UpdateProfileRequestLocal) =>
        apiFetch<MeProfileResponseLocal>('/me/profile', {
            method: 'PUT',
            body: JSON.stringify(body),
        }).then(normalizeMeProfile),
    // Estado MFA REAL del usuario (currentMethod derivado de su device preferido).
    getMfaStatus: () => apiFetch<MfaStatusResponseLocal>('/mfa/status'),
    // Métodos de 2º factor PERMITIDOS para este usuario (según la security policy de su tenant).
    // Fuente de verdad de qué métodos ofrecer en /me/security (data-driven, sin hardcode isPlatform).
    getMfaOptions: () => apiFetch<MfaOptionsResponseLocal>('/me/security/mfa/options'),

    // --- Self-service MFA (usuario autenticado, contrato real del rbac) ---
    enrollTotp: () => apiFetch<MfaEnrollTotpResponseLocal>('/mfa/enroll-totp', { method: 'POST' }),
    verifyTotpEnrollment: (body: MfaVerifyTotpEnrollmentRequestLocal) =>
        apiFetch<{ enrolled: boolean }>('/mfa/verify-totp-enrollment', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    requestEmailOtp: () => apiFetch<{ ok: true }>('/mfa/request-email-otp', { method: 'POST' }),
    // DEC-RBAC-084: reenvío self-service del OTP por SMS (análogo a request-email-otp).
    requestSmsOtp: () => apiFetch<{ ok: true }>('/mfa/request-sms-otp', { method: 'POST' }),
    // F-12: elegir EMAIL como 2º factor en primer acceso (sin body, sin OTP previo).
    chooseEmailMfa: () =>
        apiFetch<MfaChooseEmailResponseLocal>('/mfa/choose-email', { method: 'POST' }),
    // DEC-RBAC-084: elegir SMS como 2º factor (sin body, sin OTP previo — no re-verifica, igual que
    // choose-email). Solo disponible si el user tiene teléfono (el backend valida).
    chooseSmsMfa: () => apiFetch<MfaChooseSmsResponseLocal>('/mfa/choose-sms', { method: 'POST' }),
    changeMfaMethod: (body: MfaChangeMethodRequestLocal) =>
        apiFetch<{ ok: true }>('/mfa/change-method', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    // --- Identificador secundario (DEC-RBAC-081, login dual) ---
    // Inicia el alta del secundario: envía un OTP por su canal (email/SMS). PII: el `value` no se loguea.
    addSecondaryIdentifier: (body: AddSecondaryIdentifierBody) =>
        apiFetch<{ ok: true }>('/me/secondary-identifier', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    // Confirma el secundario con el OTP recibido. Si el tenant no permite secundario, el backend
    // rechaza (SecondaryNotAllowedError → 403).
    verifySecondaryIdentifier: (body: VerifySecondaryIdentifierBody) =>
        apiFetch<{ ok: true }>('/me/secondary-identifier/verify', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
};
